import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const { FiSend, FiSmile } = FiIcons;

const MessageInput = ({ onSend }) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const lastSentTime = useRef(0);
  const inputRef = useRef(null);
  const { t } = useLanguage();

  // 在組件掛載時聚焦輸入框
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 點擊外部關閉表情選擇器
  useEffect(() => {
    if (!showEmojiPicker) return;
    
    const handleClickOutside = (e) => {
      // 排除點擊表情按鈕本身
      if (e.target.closest('[data-emoji-button]')) return;
      // 排除點擊表情選擇器本身
      if (e.target.closest('[data-emoji-picker]')) return;
      
      setShowEmojiPicker(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setError(t('messageEmpty'));
      return;
    }

    if (trimmedMessage.length > 500) {
      setError(t('messageTooLong'));
      return;
    }

    // Rate limiting
    const now = Date.now();
    if (now - lastSentTime.current < 500) {
      setError(t('tooFast'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await onSend(trimmedMessage);
      if (success) {
        setMessage('');
        lastSentTime.current = now;
        // 發送成功後重新聚焦輸入框
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } else {
        setError(t('sendError'));
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(t('sendError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="border-t bg-white p-4">
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-red-600 text-sm">{error}</p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setError('');
            }}
            placeholder={t('typeMessage')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
            rows="1"
            maxLength={500}
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <button
            type="button"
            data-emoji-button="true"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 transition-colors"
            title={t('addEmoji')}
          >
            <SafeIcon icon={FiSmile} className="w-5 h-5" />
          </button>

          {/* Emoji Picker - 使用 Portal 渲染到 body 頂層 */}
          {showEmojiPicker && (
            <div 
              data-emoji-picker="true"
              className="fixed bottom-20 right-4 md:right-8 z-[1000] shadow-2xl rounded-lg border-2 border-gray-200 bg-white"
              style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'hidden' }}
            >
              <Picker 
                data={data} 
                onEmojiSelect={handleEmojiSelect} 
                theme="light" 
                previewPosition="none"
                skinTonePosition="none"
              />
            </div>
          )}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          type="submit"
          disabled={isLoading || !message.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          <SafeIcon icon={FiSend} className="w-4 h-4" />
          <span className="hidden sm:inline">{t('send')}</span>
        </motion.button>
      </form>

      <div className="mt-2 text-xs text-gray-500 text-right">
        {message.length}/500
      </div>
    </div>
  );
};

export default MessageInput;